import React, { useState } from 'react';
import { galleryData } from '../data/mock';
import { X, ZoomIn, Camera } from 'lucide-react';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const openLightbox = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'unset';
  };

  // Uniform grid - all photos same size
  const getImageSize = () => {
    return ''; // No special sizing - all uniform
  };
  
  // All photos use contain to show full image
  const getObjectPosition = () => {
    return 'object-center';
  };
  
  // All photos use contain
  const getObjectFit = () => {
    return 'object-contain bg-slate-100';
  };

  return (
    <section id="gallery" className="py-24 bg-purple-100/60 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-20 right-20 w-72 h-72 bg-[#7ed321]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#6b2d7b]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#7ed321]/20 mb-6">
            <Camera className="w-8 h-8 text-[#6b2d7b]" />
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#6b2d7b] mb-4">
            {galleryData.title}
          </h2>
          <p className="text-xl text-slate-600">
            {galleryData.subtitle}
          </p>
        </div>

        {/* Uniform Gallery Grid - All photos same size */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {galleryData.images.map((image, index) => (
            <div
              key={image.id}
              className={`group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:shadow-[#6b2d7b]/20 transition-all duration-300 cursor-pointer aspect-square`}
              onClick={() => openLightbox(image)}
            >
              {/* Image */}
              <img
                src={image.src}
                alt={image.alt}
                className={`w-full h-full ${getObjectFit()} ${getObjectPosition()} transition-transform duration-500 group-hover:scale-110`}
              />
              
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#6b2d7b]/80 via-[#6b2d7b]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-white text-lg font-semibold">{image.caption}</p>
                </div>
              </div>
              
              {/* Zoom Icon */}
              <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <ZoomIn className="w-5 h-5 text-white" />
              </div>
              
              {/* Corner Accents */}
              <div className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-[#7ed321] rounded-tl-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-[#7ed321] rounded-br-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          ))}
        </div>

        {/* Photo Count Badge */}
        <div className="text-center mt-8">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#6b2d7b]/10 text-[#6b2d7b] text-sm font-medium">
            <Camera className="w-4 h-4" />
            {galleryData.images.length} Photos
          </span>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
            onClick={closeLightbox}
          >
            <X className="w-6 h-6 text-white" />
          </button>

          {/* Navigation Arrows could be added here */}

          {/* Image Container */}
          <div
            className="relative max-w-5xl max-h-[85vh] rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="w-full h-full object-contain"
            />
            
            {/* Caption */}
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
              <p className="text-white text-xl font-semibold">{selectedImage.caption}</p>
              <p className="text-white/70 mt-1">{selectedImage.alt}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
